import React, { useState, useRef, useEffect } from 'react';
import { api } from '../../contexts/AuthContext';
import { Loader2, PhoneCall, Check, X } from 'lucide-react';

const STATUS_BADGE = {
  free: { label: 'Available', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  scheduled: { label: 'On shift', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  unavailable: { label: 'Not available', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  time_off: { label: 'Time off', cls: 'bg-red-50 text-red-600 border-red-200' },
};

const OnCallPicker = ({ date, current, onAssign, onClear }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cands, setCands] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get(`/on-call/candidates?date=${date}`);
      setCands(data.candidates || []);
    } catch { setCands([]); }
    setLoading(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle}
        className="h-8 px-3 rounded-md border border-[#DED8EB] bg-white text-sm font-semibold text-[#2D1B4E] flex items-center gap-1.5 hover:border-[#9B7ED8]"
        data-testid="oncall-picker-btn">
        <PhoneCall className="h-3.5 w-3.5 text-[#6B3FA0]" />
        {current?.employee_name || 'Assign on-call…'}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-80 max-h-96 overflow-y-auto rounded-xl border border-[#DED8EB] bg-white shadow-xl p-1.5" data-testid="oncall-picker-panel">
          {current?.employee_id && (
            <button onClick={() => { onClear(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 font-semibold"
              data-testid="oncall-clear">
              <X className="h-3.5 w-3.5" /> Clear on-call for this day
            </button>
          )}
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6B3FA0]" /></div>
          ) : cands.length === 0 ? (
            <p className="text-xs text-[#8E85A0] text-center py-4">No staff found</p>
          ) : cands.map(c => {
            const b = STATUS_BADGE[c.status] || STATUS_BADGE.free;
            const isCurrent = current?.employee_id === c.id;
            return (
              <button key={c.id} onClick={() => { onAssign(c.id); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-[#F7F4FB] ${isCurrent ? 'bg-[#F0EAFA]' : ''}`}
                data-testid={`oncall-cand-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-[#2D1B4E] truncate">{c.name}</span>
                    {isCurrent && <Check className="h-3.5 w-3.5 text-[#6B3FA0]" />}
                  </div>
                  <div className="text-[10px] text-[#8E85A0]">
                    {c.weekly_hours}h / {c.target}h this week
                    {c.shifts_today.length > 0 && ` · on shift ${c.shifts_today.join(', ')}`}
                  </div>
                </div>
                <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OnCallPicker;
